import { UserRole } from '../../../system'
import { DashboardScope } from '../../types'

// A card shown by default: just its id (placed after the previous card), or with its width / height and,
// optionally, its exact place (x: column 0-11, y: row) in the default layout
type DefaultWidget = string | { id: string, x?: number, y?: number, w?: number, h?: number }

type RoleDashboard = {
    scope: DashboardScope
    // Shown by default, in this order (it's the default layout)
    defaults: DefaultWidget[]
    // Not shown by default, but can be added with "Personalizar"
    available: string[]
}

let rolesConfig: Partial<Record<UserRole, RoleDashboard>> | undefined

// Cards of each role. A card whose screen the role can't open (it isn't in its menu) is left out anyway.
// Built the first time it's used, like the cards catalog (UserRole may not be defined yet when this file loads)
export const getDashboardRoles = (): Partial<Record<UserRole, RoleDashboard>> => {
    if (!rolesConfig) {
        rolesConfig = buildDashboardRoles()
    }
    return rolesConfig
}

const buildDashboardRoles = (): Partial<Record<UserRole, RoleDashboard>> => ({
    [UserRole.MANAGER]: {
        scope: DashboardScope.COMPANY,
        // Sales and collections on top; on the left the sales chart and the hoards, in the middle and on the
        // right the cash, debtors, sellers and stock
        defaults: [
            { id: 'salesToday', x: 0, y: 0, w: 3, h: 3 },
            { id: 'salesMonth', x: 3, y: 0, w: 3, h: 3 },
            { id: 'paymentsMonth', x: 6, y: 0, w: 3, h: 3 },
            { id: 'pendingCollections', x: 9, y: 0, w: 3, h: 3 },
            { id: 'salesChart', x: 0, y: 3, w: 6, h: 7 },
            { id: 'cashBalance', x: 6, y: 3, w: 3, h: 4 },
            { id: 'expensesMonth', x: 9, y: 3, w: 3, h: 4 },
            { id: 'topDebtors', x: 6, y: 7, w: 3, h: 7 },
            { id: 'sellerRanking', x: 9, y: 7, w: 3, h: 7 },
            { id: 'hoardsToday', x: 0, y: 10, w: 3, h: 3 },
            { id: 'budgetsMonth', x: 3, y: 10, w: 3, h: 3 },
            { id: 'expiringHoards', x: 0, y: 13, w: 6, h: 6 },
            { id: 'topRotation', x: 6, y: 14, w: 3, h: 9 },
            { id: 'criticalStock', x: 9, y: 14, w: 3, h: 9 },
            { id: 'cashFunds', x: 0, y: 19, w: 6, h: 4 }
        ],
        available: [
            'paymentsToday', 'pendingShipments', 'pendingReceptions', 'paymentOrders', 'upcomingChecks',
            'undeliveredSales', 'pendingPurchases', 'overduePurchases',
            'pickupsToday', 'shipmentPreparation', 'unassignedShipments', 'roadmapsToday'
        ]
    },
    [UserRole.SALES]: {
        scope: DashboardScope.MINE,
        defaults: [
            'salesMonth', 'salesToday', 'budgetsMonth', 'undeliveredSales',
            'salesChart',
            'expiringHoards'
        ],
        available: ['hoardsToday', 'topRotation']
    },
    [UserRole.ADMINISTRATION]: {
        scope: DashboardScope.COMPANY,
        defaults: [
            'paymentsToday', 'paymentsMonth', 'pendingCollections', 'cashBalance',
            'topDebtors', 'cashFunds',
            'expensesMonth', 'paymentOrders', 'upcomingChecks'
        ],
        available: ['salesMonth', 'salesToday', 'hoardsToday', 'salesChart', 'expiringHoards']
    },
    [UserRole.PURCHASES]: {
        scope: DashboardScope.COMPANY,
        defaults: [
            'pendingPurchases', 'pendingReceptions', 'overduePurchases',
            'criticalStock', 'topRotation'
        ],
        available: ['suppliersWithOrders']
    },
    [UserRole.WAREHOUSE]: {
        scope: DashboardScope.BRANCH,
        defaults: [
            { id: 'pickupsToday', w: 4 }, { id: 'shipmentPreparation', w: 4 }, { id: 'pendingReceptions', w: 4 },
            'criticalStock'
        ],
        available: ['undeliveredSales', 'expiringHoards', 'topRotation', 'overduePurchases', 'pendingShipments', 'roadmapsToday']
    },
    [UserRole.LOGISTICS]: {
        scope: DashboardScope.BRANCH,
        defaults: ['pendingShipments', 'unassignedShipments', 'roadmapsToday'],
        available: ['shipmentPreparation', 'activeVehicles']
    }
})
